/* eslint-disable @typescript-eslint/no-unused-vars */
import { observer } from "mobx-react-lite"
import styles from "../styles/quoteRequest.module.scss"
import {
  addCreditCardToPaymentRequest,
  getPaymentRequest,
  getUserCreditCards,
} from "../services/firebase-functions/billing"
import { useMutation, useQuery } from "@tanstack/react-query"
import { exchangeAuthToken } from "../services/firebase-functions/auth.ts"
import { getCruiseSearchResult } from "../services/firestore/cruiseSearchResult"
import BookedCruiseCard from "../components/cards/BookedCruiseCard"
import { getTripRoom } from "../services/firebase-functions/rooms"
import { makeAutoObservable } from "mobx"
import { useEffect, useMemo } from "react"
import { PassengerDetailsModal } from "../components/modals/PassengerDetailsModal"
import { ModalWrapper } from "../components/templates/ModalWrapper"
import { QuoteRoomCard } from "../components/molecules/QuoteRequest/QuoteRoomCard"
import { PaymentSummaryCard } from "../components/molecules/QuoteRequest/PaymentSummaryCard"
import { PaymentMethodCard } from "../components/molecules/QuoteRequest/PaymentMethodCard"
import { Checkbox } from "../components/atoms/Checkbox"
import CloseModalButton from "../components/buttons/CloseModalButton"
import { LoadingScreen } from "../components/templates/LoadingScreen"
import {
  ITripBookingPassenger,
  PaymentPlanEnum,
  PaymentTransaction,
  PaymentTransactionStatusEnum,
  UserID,
} from "@cruisen/contract"
import { TripRoomGuestID } from "@cruisen/contract"
import { CreditCardModal } from "../components/modals/CreditCardModal"
import { transformRoomGuestsToPassengers, UserModel } from "@cruisen/react-shared"
import { useStores } from "../models/helpers/useStores"
import { QuoteSummary } from "../components/molecules/QuoteRequest/QuoteSummary"
import { firebaseAuthUsingToken } from "../services/auth.ts"
import { fetchUserByUid } from "../services/firestore/user.ts"
import { useParams } from "react-router-dom"
import { StatusScreen } from "../components/templates/StatusScreen/index.tsx"
import { fetchAllTripUsersProfiles } from "../services/firebase-functions/profile.ts"
import { Helmet } from "react-helmet-async"

type AvailableModals = "creditCard" | "termsAndConditions"

class QuoteRequestStore {
  // next query params are optional we will get them from auth token meta
  paymentRequestId: string | null
  tripId: string | null
  cruiseId: string | null
  tripRoomId: string | null

  selectedGuestId: string | null
  selectedPaymentMethod: string | null
  termsAndConditionsAccepted: boolean
  arePassengersLoaded: boolean
  visibleModal: AvailableModals | null
  passengers: Map<TripRoomGuestID, ITripBookingPassenger>
  selectedCardId: number | null
  editCardId: number | null
  paymentType: "creditCard" | "bankAccount"
  paymentTransaction: PaymentTransaction | null

  constructor(routeParams: Record<string, string> = {}) {
    const searchParams = new URLSearchParams(window.location.search)
    this.paymentRequestId = searchParams.get("paymentRequestId")
    this.tripId = routeParams.tripId || searchParams.get("tripId")
    this.cruiseId = routeParams.cruiseId || searchParams.get("cruiseId")
    this.tripRoomId = searchParams.get("tripRoomId")

    this.selectedGuestId = null
    this.selectedPaymentMethod = "creditCardTotalPayment"
    this.termsAndConditionsAccepted = false
    this.arePassengersLoaded = false
    this.visibleModal = null
    this.passengers = new Map()
    this.selectedCardId = null
    this.editCardId = null
    this.paymentType = "creditCard"
    this.paymentTransaction = null

    makeAutoObservable(this)
  }

  setFromAuthMeta(meta?: Record<string, unknown>) {
    // populate context identifiers from auth meta
    if (!this.paymentRequestId) this.paymentRequestId = meta?.paymentRequestId?.toString()
    if (!this.tripId) this.tripId = meta?.tripId?.toString()
    if (!this.cruiseId) this.cruiseId = meta?.tripSearchResultId?.toString()
    if (!this.tripRoomId) this.tripRoomId = meta?.tripRoomId?.toString()
  }

  setSelectedGuestId(guestId: string) {
    this.selectedGuestId = guestId
  }

  setSelectedPaymentMethod(paymentMethod: string) {
    this.selectedPaymentMethod = paymentMethod
  }

  setTermsAndConditionsAccepted(accepted: boolean) {
    this.termsAndConditionsAccepted = accepted
  }

  setVisibleModal(modal: AvailableModals | null) {
    this.visibleModal = modal
  }

  setPassenger(guestId: TripRoomGuestID, passenger: ITripBookingPassenger) {
    this.passengers.set(guestId, passenger)
  }

  setEditCardId(cardId: number | null) {
    this.editCardId = cardId
  }

  setSelectedCardId(cardId: number | null) {
    this.selectedCardId = cardId
  }

  setArePassengersLoaded(arePassengersLoaded: boolean) {
    this.arePassengersLoaded = arePassengersLoaded
  }

  setPaymentTransaction(paymentTransaction: PaymentTransaction | null) {
    this.paymentTransaction = paymentTransaction
  }

  getValidPassengersAmount() {
    return [...this.passengers.values()].reduce((acc, passenger) => {
      const {
        passportNumber: _passportNumber,
        passportExpireDate: _passportExpireDate,
        passportIssueDate: _passportIssueDate,
        passportIssuingCountry: _passportIssuingCountry,
        loyaltyNumber: _loyaltyNumber,
        passportTokenId: _passportTokenId,
        ...requiredFields
      } = passenger
      if (passenger.country !== "US") {
        const { county: _, ...otherPassengerData } = requiredFields
        if (Object.values(otherPassengerData).every((value) => value !== "")) return acc + 1
      } else {
        if (Object.values(requiredFields).every((value) => value !== "")) return acc + 1
      }

      return acc
    }, 0)
  }
}

export const QuoteRequest = observer(function QuoteRequest() {
  const routeParams = useParams()
  const { token } = routeParams

  const rootStore = useStores()
  const store = useMemo(() => new QuoteRequestStore(routeParams), [token])

  const { data: loggedInUserId, isLoading: isAuthLoading } = useQuery({
    queryKey: ["exchangeAuthToken", token],
    queryFn: async (): Promise<UserID | null> => {
      // already authenticated and has all query params
      if (
        rootStore.loggedInUser &&
        store.tripId &&
        store.tripRoomId &&
        store.paymentRequestId &&
        store.cruiseId
      )
        return rootStore.loggedInUser.id

      // token in empty in url
      if (!token) return null

      const { authToken, meta } = (await exchangeAuthToken(token)) ?? {}

      const authedUser = await firebaseAuthUsingToken(authToken)
      const user = await fetchUserByUid(authedUser?.user?.uid || "")
      // user not found
      if (!user) return null

      store.setFromAuthMeta(meta)

      const loggedInUser = UserModel.create(user)
      rootStore.setProp("loggedInUser", loggedInUser)

      return loggedInUser.id
    },
  })

  const { data: cruiseData, status: cruiseSearchResultStatus } = useQuery({
    queryKey: ["cruiseSearchResult", loggedInUserId, store.tripId, store.cruiseId],
    queryFn: () => getCruiseSearchResult(store.tripId, store.cruiseId),
    enabled: !!loggedInUserId && !!store.tripId && !!store.cruiseId,
  })

  const { data: paymentRequest, isLoading: isPaymentRequestsLoading } = useQuery({
    queryKey: ["paymentRequest", loggedInUserId, store.paymentRequestId],
    queryFn: () => getPaymentRequest({ paymentRequestId: store.paymentRequestId }),
    enabled: !!loggedInUserId && !!store.paymentRequestId,
  })

  const { data: tripRoomData, isLoading: isTripRoomLoading } = useQuery({
    queryKey: ["tripRoom", loggedInUserId, store.tripId, store.tripRoomId],
    queryFn: () => getTripRoom({ tripId: store.tripId, tripRoomId: store.tripRoomId }),
    enabled: !!loggedInUserId && !!store.tripId && !!store.tripRoomId,
  })

  const { data: existingCreditCards } = useQuery({
    queryKey: ["creditCards", loggedInUserId],
    queryFn: () => getUserCreditCards(),
    initialData: [],
    staleTime: 0,
    enabled: !!loggedInUserId,
  })

  const { data: profiles, isLoading: isProfilesLoading } = useQuery({
    queryKey: ["allProfiles", loggedInUserId, store.tripId],
    queryFn: () => fetchAllTripUsersProfiles(store.tripId),
    initialData: [],
    staleTime: 0,
    enabled: !!loggedInUserId && !!store.tripId,
  })

  const selectedCard = existingCreditCards.find((card) => card.id === store.selectedCardId)
  const editCard = existingCreditCards.find((card) => card.id === store.editCardId)
  const pickedCruise = cruiseData?.cruiseSearchResult
  const tripRoom = tripRoomData?.tripRoom
  const lineItem = paymentRequest?.lineItems?.[0]

  const primaryUserData = store.passengers.get(loggedInUserId) || null
  const chosenPassenger = store.selectedGuestId && store.passengers.get(store.selectedGuestId)
  const allGuests = tripRoom?.guests.map((guest) => ({ ...guest, roomId: tripRoom.id })) || []

  const validPassengersAmount = store.getValidPassengersAmount()
  const isSomePassengersMissingData = validPassengersAmount < allGuests.length

  const cvcExpired = selectedCard?.signatureExpired
  const shouldAddCVCNumber = store.paymentType === "creditCard" && cvcExpired

  const isTermsAccepted = paymentRequest?.termsAndConditions
    ? store.termsAndConditionsAccepted
    : true

  const { mutate: submitPayment, isPending: isSubmittingPayment } = useMutation({
    mutationFn: async () => {
      const paymentPlan =
        store.selectedPaymentMethod === "creditCardTotalPayment"
          ? PaymentPlanEnum.Total
          : PaymentPlanEnum.DepositOnly

      const result = await addCreditCardToPaymentRequest({
        paymentRequestId: paymentRequest.id,
        lineItemId: lineItem.id,
        creditCardId: selectedCard.id,
        paymentPlan,
        amount: paymentPlan === PaymentPlanEnum.Total ? lineItem.total : lineItem.deposit,
        currency: lineItem.currency,
      })

      if (
        result.status === PaymentTransactionStatusEnum.Approved ||
        result.status === PaymentTransactionStatusEnum.Succeeded
      ) {
        store.setPaymentTransaction(result)
      } else if (
        result.status === PaymentTransactionStatusEnum.Failed ||
        result.status === PaymentTransactionStatusEnum.ACHFailed
      ) {
        alert("Payment failed, try again later")
      }
    },
  })

  const roomGuests = tripRoom?.guests.map((guest) => {
    const passengerData = store.passengers.get(guest.userId || guest.id)

    return { ...guest, passengerData }
  })

  const isSubmitDisabled =
    isSomePassengersMissingData ||
    !selectedCard ||
    shouldAddCVCNumber ||
    isSubmittingPayment ||
    !isTermsAccepted

  const getButtonText = () => {
    if (isSubmittingPayment) {
      return "Loading..."
    }
    if (isSomePassengersMissingData) {
      return "Missing passenger data"
    }
    if (!selectedCard || shouldAddCVCNumber) {
      return "Add credit card details"
    }
    if (!isTermsAccepted) {
      return "Accept Terms and Conditions"
    }
    return "Authorize and accept"
  }

  const generateErrorDetails = (): { title: string | null; description: string | null } => {
    if (!token) {
      return {
        title: "Invalid url",
        description: "Please check the url and try again",
      }
    } else if (!isAuthLoading && !rootStore.loggedInUser?.id) {
      return {
        title: "User not found",
        description: "Please check the url and try again",
      }
    } else if (!isPaymentRequestsLoading && !paymentRequest) {
      return {
        title: "Quote request not found",
        description: "Please check the url and try again",
      }
    } else if (paymentRequest && paymentRequest.status !== "requiresPaymentMethod") {
      return {
        title: "Quote request is expired",
        description: "This payment request is already filled out or has been rejected",
      }
    } else {
      return {
        title: null,
        description: null,
      }
    }
  }

  const buttonText = getButtonText()

  const areProfilesLoaded = profiles?.length > 0 && !isProfilesLoading

  useEffect(() => {
    if (!tripRoomData || !areProfilesLoaded) return

    transformRoomGuestsToPassengers({
      allGuests,
      profiles,
      bookingRooms: [],
      setPassengerData: (key, value) => store.setPassenger(key, value),
    })

    store.setArePassengersLoaded(true)
  }, [tripRoomData, areProfilesLoaded])

  useEffect(() => {
    if (existingCreditCards.length === 0) return
    store.setSelectedCardId(existingCreditCards[0].id)
  }, [existingCreditCards.length])

  const isLoading =
    isAuthLoading ||
    isPaymentRequestsLoading ||
    cruiseSearchResultStatus === "pending" ||
    isTripRoomLoading

  if (store.paymentTransaction) {
    return (
      <QuoteSummary
        paymentRequest={paymentRequest}
        paymentTransaction={store.paymentTransaction}
        lineItemId={lineItem.id}
      />
    )
  }

  if (isLoading) {
    return <LoadingScreen />
  }

  const { title, description } = generateErrorDetails()

  if (title && description) {
    return <StatusScreen title={title} description={description} error={true} />
  }

  return (
    <div
      className={styles.pageContainer}
      data-modal-visible={store.visibleModal !== null || chosenPassenger ? "true" : "false"}
    >
      <Helmet>
        <title>Quote request</title>
        <meta name="viewport" content="width=device-width, maximum-scale=1.0" />
      </Helmet>
      <h1 className={styles.title}>Quote request</h1>
      <BookedCruiseCard
        cruise={{
          image: pickedCruise.heroImage,
          lineLogoImage: pickedCruise.lineLogo,
          lineName: pickedCruise.lineName,
          name: pickedCruise.name,
          startDate: pickedCruise.startDate,
          endDate: pickedCruise.endDate,
          ports: pickedCruise.ports,
        }}
        onClick={() => undefined}
        lightBackground
      />
      <h2 className={styles.sectionTitle}>Cabins</h2>
      {tripRoom ? (
        <QuoteRoomCard
          cruiseDetails={{
            deckName: lineItem.meta?.deckName,
            cabinNo: lineItem.meta?.cabinNo,
            cabinGradeName: lineItem.meta?.cabinGradeName,
          }}
          tripRoomName={tripRoom.name}
          roomGuests={roomGuests}
          arePassengersLoaded={store.arePassengersLoaded}
          onGuestClick={(guestId) => store.setSelectedGuestId(guestId)}
        />
      ) : null}
      <h2 className={styles.sectionTitle}>Payment details</h2>
      {lineItem ? <PaymentSummaryCard lineItem={lineItem} /> : null}
      <h2 className={styles.sectionTitle}>Payment method</h2>
      {lineItem ? (
        <PaymentMethodCard
          paymentType="creditCard"
          selectedPaymentMethod={store.selectedPaymentMethod}
          onPaymentTypeChange={(_, paymentType) => store.setSelectedPaymentMethod(paymentType)}
          lineItem={{
            total: Number(lineItem.total),
            deposit: Number(lineItem.deposit),
            totalDueDate: new Date(lineItem.totalDueDate).toISOString(),
          }}
          existingCreditCards={existingCreditCards}
          onAddCard={() => {
            store.setVisibleModal("creditCard")
          }}
          onEditCard={(cardId) => {
            store.setEditCardId(Number(cardId))
            store.setVisibleModal("creditCard")
          }}
          selectedCardId={String(store.selectedCardId)}
          onCardSelect={(cardId) => store.setSelectedCardId(Number(cardId))}
        />
      ) : null}
      {paymentRequest?.termsAndConditions ? (
        <div className={styles.termsContainer}>
          <Checkbox
            isChecked={store.termsAndConditionsAccepted}
            onChange={(accepted) => {
              if (store.visibleModal === "termsAndConditions") return
              store.setTermsAndConditionsAccepted(accepted)
            }}
          >
            <p className={styles.termsText}>
              I have read and agree to the{" "}
              <span
                className={styles.termsTextButton}
                onClick={() => store.setVisibleModal("termsAndConditions")}
              >
                Terms and Conditions
              </span>{" "}
              of this quote.
            </p>
          </Checkbox>
        </div>
      ) : null}
      <button
        className={styles.submitButton}
        disabled={isSubmitDisabled}
        onClick={() => submitPayment()}
      >
        {buttonText}
      </button>
      {chosenPassenger ? (
        <ModalWrapper>
          <PassengerDetailsModal
            defaultData={chosenPassenger}
            tripId={store.tripId}
            cruiseLineId={pickedCruise.lineId}
            lineName={pickedCruise.lineName}
            loggedInUserId={loggedInUserId}
            onClose={() => store.setSelectedGuestId(null)}
            guestRoomId={store.tripRoomId}
            primaryProfile={primaryUserData}
            onSubmit={(passengerData) => {
              store.setPassenger(store.selectedGuestId, passengerData)
              store.setSelectedGuestId(null)
            }}
          />
        </ModalWrapper>
      ) : null}
      {store.visibleModal === "termsAndConditions" ? (
        <ModalWrapper>
          <div className={styles.termsAndConditionsModal}>
            <div className={styles.termsAndConditionsModalHeading}>
              <h2 className={styles.termsAndConditionsModalTitle}>Terms and Conditions</h2>
              <CloseModalButton onClick={() => store.setVisibleModal(null)} />
            </div>
            <p
              className={styles.termsAndConditionsModalText}
              dangerouslySetInnerHTML={{ __html: paymentRequest?.termsAndConditions }}
            />
          </div>
        </ModalWrapper>
      ) : null}
      {store.visibleModal === "creditCard" ? (
        <ModalWrapper>
          <CreditCardModal
            defaultData={editCard || null}
            loggedInUserId={loggedInUserId}
            onClose={() => {
              store.setVisibleModal(null)
              store.setEditCardId(null)
            }}
            onSubmit={() => {
              store.setVisibleModal(null)
              store.setEditCardId(null)
            }}
          />
        </ModalWrapper>
      ) : null}
    </div>
  )
})
